import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import AddressLookup from "@/components/AddressLookup";
import { toast } from "sonner";
import { Loader2, X, ChevronDown, FolderKanban } from "lucide-react";

const selCls = "block w-full appearance-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 pr-9 text-sm text-slate-900 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none";
const areaCls = "mt-1 w-full px-3 py-2 text-sm rounded-lg bg-slate-50 border border-slate-200 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400";

function Lbl({ children }) {
  return <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{children}</span>;
}

export default function ProjectForm({ mode = "create", project, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [busy, setBusy] = useState(false);
  const [owners, setOwners] = useState([]);
  const [statuses, setStatuses] = useState(["Planning", "Active", "On Hold", "Completed", "Cancelled"]);
  const [f, setF] = useState({
    name: project?.name || "",
    customer_name: project?.customer_name || "",
    customer_legacy_id: project?.customer_legacy_id || "",
    line1: project?.site_address?.line1 || "",
    line2: project?.site_address?.line2 || "",
    city: project?.site_address?.city || "",
    postcode: project?.site_address?.postcode || "",
    description: project?.description || "",
    owner_id: project?.owner_id || "",
    owner_name: project?.owner_name || "",
    status: project?.status || "Planning",
    target_start: project?.target_start || "",
    target_end: project?.target_end || "",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const onLookup = (a) => setF((s) => ({ ...s, line1: a.address_line_1 || "", line2: a.address_line_2 || a.county || "", city: a.town_city || "", postcode: a.postcode || "" }));

  const [custResults, setCustResults] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const justPicked = useRef(false);

  useEffect(() => {
    api.get("/projects/meta/options").then((r) => {
      setOwners(r.data.owners || []);
      setStatuses(r.data.statuses || statuses);
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const q = (f.customer_name || "").trim();
    if (justPicked.current) { justPicked.current = false; return; }
    if (q.length < 2) { setCustResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get("/customers/search", { params: { q } }); setCustResults(r.data.items || []); setCustOpen(true); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [f.customer_name]);

  const pickCustomer = (c) => {
    justPicked.current = true;
    setF((s) => ({ ...s, customer_name: c.full_name || c.company || "", customer_legacy_id: c.legacy_id || "" }));
    setCustOpen(false);
  };

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Project name is required"); return; }
    setBusy(true);
    const owner = owners.find((o) => o.id === f.owner_id);
    const payload = {
      name: f.name.trim(),
      customer_name: f.customer_name || null,
      customer_legacy_id: f.customer_legacy_id || null,
      site_address: { line1: f.line1, line2: f.line2, city: f.city, postcode: f.postcode, country: "United Kingdom" },
      description: f.description || null,
      owner_id: f.owner_id || null,
      owner_name: owner ? owner.name : (f.owner_name || null),
      status: f.status,
      target_start: f.target_start || null,
      target_end: f.target_end || null,
    };
    try {
      if (isEdit) {
        await api.patch(`/projects/${project.project_id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created");
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save project");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="project-form-modal">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><FolderKanban className="w-5 h-5" /></span>
            <h2 className="text-base font-bold text-slate-900">{isEdit ? "Edit project" : "New project"}</h2>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="project-form-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <Lbl>Project name</Lbl>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" placeholder="e.g. Garden Wellness Centre" data-testid="project-name-input" />
          </div>

          <div className="relative">
            <Lbl>Customer</Lbl>
            <Input value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} onFocus={() => custResults.length && setCustOpen(true)} className="mt-1" placeholder="Search customers…" data-testid="project-customer-input" />
            {custOpen && custResults.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-auto" data-testid="project-customer-results">
                {custResults.map((c) => (
                  <button key={c.legacy_id} onClick={() => pickCustomer(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 text-slate-700">
                    <span className="font-medium">{c.full_name || c.company}</span>
                    {c.company && c.full_name && <span className="text-slate-400"> · {c.company}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="mb-3"><AddressLookup onSelect={onLookup} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Lbl>Site address line 1</Lbl><Input value={f.line1} onChange={(e) => set("line1", e.target.value)} className="mt-1" data-testid="project-line1-input" /></div>
            <div><Lbl>Line 2</Lbl><Input value={f.line2} onChange={(e) => set("line2", e.target.value)} className="mt-1" /></div>
            <div><Lbl>City</Lbl><Input value={f.city} onChange={(e) => set("city", e.target.value)} className="mt-1" /></div>
            <div><Lbl>Postcode</Lbl><Input value={f.postcode} onChange={(e) => set("postcode", e.target.value)} className="mt-1" data-testid="project-postcode-input" /></div>
          </div>

          <div>
            <Lbl>Description</Lbl>
            <textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={3} className={areaCls} data-testid="project-description-input" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Lbl>Owner</Lbl>
              <div className="relative mt-1">
                <select value={f.owner_id} onChange={(e) => set("owner_id", e.target.value)} className={selCls} data-testid="project-owner-select">
                  <option value="">Unassigned</option>
                  {owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
            </div>
            <div>
              <Lbl>Status</Lbl>
              <div className="relative mt-1">
                <select value={f.status} onChange={(e) => set("status", e.target.value)} className={selCls} data-testid="project-status-select">
                  {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Lbl>Start</Lbl><input type="date" value={f.target_start} onChange={(e) => set("target_start", e.target.value)} className={selCls.replace("pr-9", "")} data-testid="project-start-input" /></div>
              <div><Lbl>End</Lbl><input type="date" value={f.target_end} onChange={(e) => set("target_end", e.target.value)} className={selCls.replace("pr-9", "")} data-testid="project-end-input" /></div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
          <Button variant="outline" onClick={onClose} data-testid="project-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" data-testid="project-form-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? "Save changes" : "Create project"}
          </Button>
        </div>
      </div>
    </div>
  );
}
